import { DateRangePicker, Icons } from '@commenda/kakashi';
import { useEffect, useState } from 'react';

interface IndirectTaxFilingsPeriodPickerProps {
  fromDate: string;
  toDate: string;
  onChange: (fromDate: string, toDate: string) => void;
}

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatRangeLabel(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return 'Select period';
  }

  // from/to are date-only values (e.g. "2026-06-01") parsed as UTC midnight;
  // read them in UTC so users behind UTC don't see the label slip to the
  // previous month (PLAT-2537).
  const fromLabel = `${MONTH_NAMES_SHORT[from.getUTCMonth()]} ${from.getUTCFullYear()}`;
  const toLabel = `${MONTH_NAMES_SHORT[to.getUTCMonth()]} ${to.getUTCFullYear()}`;

  return fromLabel === toLabel ? fromLabel : `${fromLabel} \u2013 ${toLabel}`;
}

const IndirectTaxFilingsPeriodPicker = ({
  fromDate,
  toDate,
  onChange,
}: IndirectTaxFilingsPeriodPickerProps) => {
  // Local transient state: DateRangePicker drives partial selections (first
  // click sets fromDate only; second click completes the range). We mirror
  // those partials here so the picker's own state stays in sync, and only
  // bubble up to the parent once both dates are present.
  const [draftFromDate, setDraftFromDate] = useState<string | undefined>(
    fromDate
  );
  const [draftToDate, setDraftToDate] = useState<string | undefined>(toDate);

  // When the parent commits a new complete range (e.g., after we called
  // onChange, or on mount), reset the draft to match.
  useEffect(() => {
    setDraftFromDate(fromDate);
    setDraftToDate(toDate);
  }, [fromDate, toDate]);

  return (
    <DateRangePicker
      label="Filings"
      fromDate={draftFromDate}
      toDate={draftToDate}
      onDateChange={(from, to) => {
        setDraftFromDate(from);
        setDraftToDate(to);
        if (from && to) {
          onChange(from, to);
        }
      }}
      renderTrigger={({ onClick }) => (
        <button
          type="button"
          onClick={onClick}
          className="flex items-center gap-xl rounded-sm border px-md py-sm bg-bg-primary txt-small-medium text-text-primary transition-colors hover:border-border-primary-hover"
        >
          <Icons.Calendar className="size-4 text-text-tertiary" />
          <span>{formatRangeLabel(fromDate, toDate)}</span>
          <Icons.ChevronDown className="size-4 text-text-tertiary" />
        </button>
      )}
    />
  );
};

export { IndirectTaxFilingsPeriodPicker };
